import * as React from 'react';
import { CustomerInterface } from '../../Interfaces/CustomerInterface';
import { l } from '../../Other/Translation';
import { Modal } from '../common/Modal/Modal';
import { Input } from '../common/Input/Input';
import { Button, ButtonType } from '../common/Button/Button';

enum CustomerField {
  LASTNAME = 'lastName',
  FIRSTNAME = 'firstName',
  COMPANYNAME = 'companyName',
  BUSINESSID = 'businessId',
  VATNUMBER = 'vatNumber',
  STREETADDRESS1 = 'streetAddress1',
  STREETADDRESS2 = 'streetAddress2',
  CITY = 'city',
  ZIPCODE = 'pobox'
}

enum CustomerLabel {
  LASTNAME = 'LastName',
  FIRSTNAME = 'FirstName',
  COMPANYNAME = 'CompanyName',
  BUSINESSID = 'BusinessId',
  VATNUMBER = 'company.VatNumber',
  STREETADDRESS1 = 'Address',
  STREETADDRESS2 = 'streetAddress2',
  CITY = 'Municipality',
  ZIPCODE = 'PostalCode'
}

export const newCustomer: CustomerInterface = {
  id: null,
  companyName: '',
  address: {
    streetAddress1: '',
    streetAddress2: '',
    pobox: '',
    city: '',
    country: ''
  },
  firstName: '',
  lastName: '',
  businessId: '',
  customerType: 'c',
  name: '',
  displayName: '',
  email: '',
  vatNumber: '',
  phoneNumberHome: '',
  phoneNumberWork: '',
  title: '',
  paymentTerm: 0,
  language: '',
  vatPct: 0,
};

interface ExternalProps {
  isOpen: boolean;
  close: () => void;
  customer?: CustomerInterface;
  saveCustomer: (customer: CustomerInterface) => void;
}

interface StateProps {
  localCustomer: CustomerInterface;
}

export class CustomerModal extends React.Component<ExternalProps, StateProps> {

  constructor(props) {
    super(props);
    const hasCustomer = Boolean(props.customer && props.customer.customerType);
    this.state = {
      localCustomer: hasCustomer ? props.customer : newCustomer
    };
  }

  private close() {
    this.props.close();
  }

  private renderInput(field: CustomerField, label: CustomerLabel, numeric = false, onChange = null): JSX.Element {
    let value = this.state.localCustomer[field] ?
      (numeric ? this.state.localCustomer[field].toString() : this.state.localCustomer[field])
      : null;
    if (value === null) {
      value = '';
    }
    return (<Input label={l(label)}
      value={value}
      automation={field}
      onBlur={(newValue) => {
        if (onChange && typeof onChange === 'function') {
          onChange(newValue);
        } else {
          if (numeric) {
            this.setState({ localCustomer: { ...this.state.localCustomer, [field]: parseInt(newValue, 10) } });
          } else {
            this.setState({ localCustomer: { ...this.state.localCustomer, [field]: newValue } });
          }
        }
      }}
    />);
  }
  private renderCompanyNameField() {
    return this.renderInput(CustomerField.COMPANYNAME, CustomerLabel.COMPANYNAME);
  }

  private renderAddressInput(label: string, fieldName: CustomerField) {
    return (
      <Input label={label}
        value={this.state.localCustomer.address[fieldName]}
        automation={fieldName}
        onBlur={(value) => {
          this.setState({
            localCustomer: {
              ...this.state.localCustomer,
              address: { ...this.state.localCustomer.address, [fieldName]: value }
            }
          });
        }}
      />
    );
  }

  public render(): JSX.Element {
    return (
      <Modal
        isOpen={this.props.isOpen}
        header={this.props.customer ? l('Customer') : l('AddCustomer')}
        automation={'customerModal'}
        className="CustomerModal"
        size={'medium'}
        showCloseIcon={true}
        onClose={() => this.close()}
      >
        <div className="contactDetails tight">
          <h2>{l('customer.ContactDetails')}</h2>

          {this.renderInput(CustomerField.COMPANYNAME, CustomerLabel.COMPANYNAME)}

          {this.renderInput(CustomerField.BUSINESSID, CustomerLabel.BUSINESSID, false, (value) => {
            this.setState({
              localCustomer: {
                ...this.state.localCustomer,
                businessId: value,
              }
            });
          })
          }
          {this.renderInput(CustomerField.VATNUMBER, CustomerLabel.VATNUMBER, false, (value) => {
            this.setState({
              localCustomer: {
                ...this.state.localCustomer,
                vatNumber: value,
              }
            });
          })
          }

          {this.renderInput(CustomerField.FIRSTNAME, CustomerLabel.FIRSTNAME)}
          {this.renderInput(CustomerField.LASTNAME, CustomerLabel.LASTNAME)}
        </div>

        <div className="invoicingAddress tight">
          <h2>{l('customer.Addresses')}</h2>
          {this.renderAddressInput(l('Address'), CustomerField.STREETADDRESS1)}
          {this.renderAddressInput('', CustomerField.STREETADDRESS2)}
          {this.renderAddressInput(l('PostalCode'), CustomerField.ZIPCODE)}
          {this.renderAddressInput(l('Municipality'), CustomerField.CITY)}
        </div>

        <footer>
          <Button
            label={l('Save')}
            type={ButtonType.normal}
            action={() => {
              const customerData = this.state.localCustomer;
              this.props.saveCustomer(customerData);
              this.close();
            }}
            automation="customer-form-save"
          />
          <Button
            label={l('Cancel')}
            type={ButtonType.cancel}
            action={() => {
              this.close();
            }}
            automation="customer-form-cancel"
          />
        </footer>
      </Modal>
    );
  }
}
